"use client";

/**
 * Agenda Day Block
 * One day in the stacked Agenda layout: the day heading in the section accent, the organizer's
 * summary under it, then the day's items as hairline-separated rows straight on the section.
 * Live "Now" and "Starts in" pills only light up on today's rows.
 */

import type React from "react";

import { useScheduleLiveMarkers } from "../../../../_hooks/useScheduleLiveMarkers";
import type { AgendaDay } from "../../../../_logic/utils/schedule-agenda";
import { formatScheduleDayHeading } from "../../../../_logic/utils/schedule-display-format";
import { ScheduleRow, type ScheduleRowConfig } from "./ScheduleRow";

const NO_ITEMS: AgendaDay["items"] = [];

interface AgendaDayBlockProps {
  day: AgendaDay;
  /** Row render context; its `surface` should be "section". */
  rowConfig: ScheduleRowConfig;
  /** Whether this day is today in the event's zone (only today's rows get live pills). */
  isToday: boolean;
  timeZone: string | null;
}

/**
 * Renders one agenda day.
 *
 * @param props - See AgendaDayBlockProps.
 * @returns The day section.
 */
export function AgendaDayBlock({ day, rowConfig, isToday, timeZone }: AgendaDayBlockProps): React.ReactElement {
  const { liveItemIds, upcomingMinutesById } = useScheduleLiveMarkers(isToday ? day.items : NO_ITEMS, timeZone);

  return (
    <section className="flex flex-col gap-4" aria-label={formatScheduleDayHeading(day.date)}>
      <div className="flex flex-col gap-1.5">
        <h3 className="text-2xl font-bold tracking-tight text-(--lp-accent,#4338ca)">
          {formatScheduleDayHeading(day.date)}
        </h3>
        {day.summary && (
          <p className="whitespace-pre-line text-lg font-medium text-(--lp-text,#262626)">{day.summary}</p>
        )}
      </div>
      {day.items.length > 0 && (
        <ul className="flex list-none flex-col divide-y divide-current/10 border-y border-current/10 text-(--lp-text,#171717)">
          {day.items.map((item) => (
            <li key={item.id}>
              <ScheduleRow
                config={rowConfig}
                item={item}
                isLive={liveItemIds.has(item.id)}
                startsInMinutes={upcomingMinutesById.get(item.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
